import { useEffect, useRef, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import {
  Building2,
  BarChart3,
  Users,
  Truck,
  Shield,
  CheckCircle,
  ArrowRight,
  Package,
  Wrench,
  UserCheck,
  ClipboardList,
  Warehouse,
  Car,
  Landmark,
  CreditCard,
  WifiOff,
  Zap,
  ScanLine,
  Boxes,
  FileText,
  Route,
  Container,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Footer } from '@/sections/Footer';
import { SEO } from '@/components/SEO';
import { submitForm } from '@/lib/forms';

gsap.registerPlugin(ScrollTrigger);

const dashboardFeatures = [
  {
    icon: Package,
    title: 'Gestión de envíos',
    description: 'Crea, visualiza y edita paquetes, clientes y solicitudes de servicio, con etiquetas QR por artículo y facturas en PDF.',
  },
  {
    icon: Landmark,
    title: 'Aduana inteligente',
    description: 'El arancel oficial cubano integrado, con cálculo de cargos por paquete y tus precios por agencia.',
  },
  {
    icon: CreditCard,
    title: 'Registro de cobros',
    description: 'Efectivo, Zelle y saldo pendiente por paquete, con recibo en PDF.',
  },
  {
    icon: Warehouse,
    title: 'Sucursales',
    description: 'Gestiona múltiples ubicaciones desde un solo lugar.',
  },
  {
    icon: Car,
    title: 'Vehículos',
    description: 'Controla tu flota de transporte y asignaciones.',
  },
  {
    icon: Users,
    title: 'Tu equipo, con permisos',
    description: 'Permisos granulares por empleado: cada quien ve y hace solo lo que le corresponde.',
  },
];

const adminAppFeatures = [
  {
    icon: ScanLine,
    title: 'Escaneo masivo en almacén',
    description: 'Lee las etiquetas QR al recibir o despachar y actualiza cientos de artículos de una sola vez.',
  },
  {
    icon: WifiOff,
    title: 'Modo sin conexión',
    description: 'Tu equipo escanea y entrega sin señal; todo se sincroniza al reconectar.',
  },
  {
    icon: ClipboardList,
    title: 'Confirmaciones de entrega',
    description: 'Foto, firma del receptor y GPS en cada entrega.',
  },
  {
    icon: UserCheck,
    title: 'Asignación de entregas',
    description: 'Asigna entregas a repartidores por ruta y sigue el avance en tiempo real.',
  },
  {
    icon: Users,
    title: 'Gestión de trabajadores',
    description: 'Administra información de repartidores y personal.',
  },
  {
    icon: Wrench,
    title: 'Flota: mantenimiento y combustible',
    description: 'Historial de mantenimiento, control de uso y registro de combustible por vehículo.',
  },
];

const transitariaFeatures = [
  {
    icon: Warehouse,
    title: 'Recepción por lote',
    description: 'Escaneo masivo al recibir la carga de cada agencia: cientos de artículos actualizados de una sola vez.',
  },
  {
    icon: Boxes,
    title: 'Consolidación por destino',
    description: 'Agrupa la carga de todas tus agencias por destino y método de envío.',
  },
  {
    icon: FileText,
    title: 'Manifiestos de contenedor',
    description: 'Arma y exporta el manifiesto desde la misma carga que escaneaste — sin volver a teclear.',
  },
  {
    icon: Route,
    title: 'Trazabilidad por traspaso',
    description: 'Recepción, despacho y llegada quedan registrados y visibles para la agencia y su cliente.',
  },
  {
    icon: FileText,
    title: 'Facturación a tus agencias',
    description: 'Factura a cada agencia por la carga que te envía, con desglose por manifiesto y recibo en PDF.',
  },
  {
    icon: Building2,
    title: 'Tu red de agencias',
    description: 'Cada agencia sigue operando en su propio panel; tú ves y cobras el conjunto.',
  },
];

const ecosystemPlatforms = [
  {
    to: '/plataforma/mi-dashboard',
    logo: '/logos/apps/dashboard.png',
    alt: 'Logo de Mi Dashboard',
    title: 'Mi Dashboard',
    description: 'El panel web de tu agencia: centro operativo para el control total.',
  },
  {
    to: '/plataforma/mi-envio-cuba',
    logo: '/logos/apps/mi-envio-cuba.png',
    alt: 'Logo de la app Mi Envío Cuba',
    title: 'Mi Envío Cuba',
    description: 'La app donde tus clientes rastrean sus envíos, solicitan servicios y te escriben.',
  },
  {
    to: '/plataforma/mi-admin',
    logo: '/logos/apps/scanner.png',
    alt: 'Logo de Mi Admin',
    title: 'Mi Admin',
    description: 'La app de almacén y reparto: escaneo QR, prueba de entrega y modo sin conexión.',
  },
  {
    to: '/plataforma/mi-restaurante',
    logo: '/logos/apps/restaurant.png',
    alt: 'Logo de Mi Restaurante',
    title: 'Mi Restaurante',
    description: 'Para comercios aliados de tu agencia. En camino.',
  },
  {
    to: '/plataforma/mi-cargo',
    logo: '/logos/apps/cargo.png',
    alt: 'Logo de Mi Cargo',
    title: 'Mi Cargo',
    description: 'Consolidación, manifiestos y trazabilidad para transitarias. En camino.',
  },
];

const benefits = [
  {
    icon: BarChart3,
    title: 'Dashboard completo',
    description:
      'Control total de operaciones desde un solo lugar.',
  },
  {
    icon: Users,
    title: 'Presencia digital',
    description:
      'Tu agencia visible en la app Mi Envío Cuba, con tus tarifas, sucursales y servicios.',
  },
  {
    icon: Truck,
    title: 'Gestión de flota',
    description:
      'Controla vehículos, rutas y repartidores desde la plataforma.',
  },
  {
    icon: Shield,
    title: 'Proceso de verificación',
    description:
      'Documentación legal, ubicación física y auditoría dentro del sistema.',
  },
];

const requirements = [
  'Documentación legal vigente',
  'Ubicación física verificable',
  'Capacidad de procesamiento de envíos',
  'Compromiso con estándares de calidad',
];

const pricingPlans = [
  {
    name: 'Plan Estándar',
    subtitle: 'TODO INCLUIDO',
    price: 'Gratis',
    period: ' tus primeros 6 meses',
    icon: Zap,
    color: 'bg-brand',
    borderColor: 'border-brand/30',
    featured: true,
    features: [
      { title: 'Después de los primeros 6 meses: $600 al año. Un solo plan, sin niveles ni sorpresas.', items: [] },
      { title: 'Mi Dashboard', items: ['Clientes y destinatarios con búsqueda instantánea', 'Paquetes con etiquetas QR y facturas en PDF', 'Aduana inteligente: el arancel oficial con tus precios', 'Solicitudes y colas de servicio', 'Sucursales ilimitadas y gestión de flota'] },
      { title: 'Comunicación', items: ['Bandeja única con todos los chats de tus clientes', 'Chat interno para tu equipo, separado de los clientes'] },
      { title: 'App para tus clientes (Mi Envío Cuba)', items: ['Rastreo en tiempo real, artículo por artículo', 'Prueba de entrega: foto, firma y GPS', 'Programar recogida, Casillero Envíos y Cargos aduanales'] },
      { title: 'App de almacén y reparto (Mi Admin)', items: ['Escaneo QR y actualización masiva en almacén', 'Modo sin conexión con sincronización automática'] },
      { title: 'Control y seguridad', items: ['Permisos granulares por empleado', 'Datos cifrados en reposo', 'Registro de auditoría inalterable'] },
      { title: 'Soporte', items: ['Onboarding guiado por el equipo MEC', 'Soporte continuo'] },
    ],
    ideal: 'Cualquier agencia: desde una sucursal hasta una operación multi-ciudad.',
  },
];

export function Agencias() {
  const location = useLocation();
  const incomingPlan = (location.state as { plan?: string } | null)?.plan ?? '';
  const [form, setForm] = useState({ nombre: '', empresa: '', email: '', telefono: '', ciudad: '', plan: incomingPlan, mensaje: '' });
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  // Pre-select plan and scroll to form if navigated with state
  useEffect(() => {
    if (incomingPlan) {
      setForm(prev => ({ ...prev, plan: incomingPlan }));
      setTimeout(() => {
        document.getElementById('solicitar')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 300);
    }
  }, [incomingPlan]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('loading');
    try {
      await submitForm('agencias', form);
      setStatus('success');
    } catch (err) {
      console.error('[Agencias] form submission failed:', err);
      setStatus('error');
    }
  };

  const headerRef = useRef<HTMLDivElement>(null);
  const dashboardRef = useRef<HTMLDivElement>(null);
  const adminAppRef = useRef<HTMLDivElement>(null);
  const transitariasRef = useRef<HTMLDivElement>(null);
  const ecosystemRef = useRef<HTMLDivElement>(null);
  const pricingRef = useRef<HTMLDivElement>(null);
  const benefitsRef = useRef<HTMLDivElement>(null);
  const requirementsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        headerRef.current,
        { y: 30, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.6,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: headerRef.current,
            start: 'top 80%',
          },
        }
      );

      const dashboardCards = dashboardRef.current?.querySelectorAll('.feature-card');
      if (dashboardCards) {
        gsap.fromTo(
          dashboardCards,
          { y: 40, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            stagger: 0.1,
            duration: 0.5,
            ease: 'power2.out',
            scrollTrigger: {
              trigger: dashboardRef.current,
              start: 'top 75%',
            },
          }
        );
      }

      const adminCards = adminAppRef.current?.querySelectorAll('.admin-card');
      if (adminCards) {
        gsap.fromTo(
          adminCards,
          { y: 30, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            stagger: 0.08,
            duration: 0.5,
            ease: 'power2.out',
            scrollTrigger: {
              trigger: adminAppRef.current,
              start: 'top 75%',
            },
          }
        );
      }

      const transitariaCards = transitariasRef.current?.querySelectorAll('.transitaria-card');
      if (transitariaCards) {
        gsap.fromTo(
          transitariaCards,
          { y: 30, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            stagger: 0.08,
            duration: 0.5,
            ease: 'power2.out',
            scrollTrigger: {
              trigger: transitariasRef.current,
              start: 'top 75%',
            },
          }
        );
      }

      const ecosystemCards = ecosystemRef.current?.querySelectorAll('.ecosystem-card');
      if (ecosystemCards) {
        gsap.fromTo(
          ecosystemCards,
          { y: 30, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            stagger: 0.08,
            duration: 0.5,
            ease: 'power2.out',
            scrollTrigger: {
              trigger: ecosystemRef.current,
              start: 'top 80%',
            },
          }
        );
      }

      const pricingCards = pricingRef.current?.querySelectorAll('.pricing-card');
      if (pricingCards) {
        gsap.fromTo(
          pricingCards,
          { y: 40, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            stagger: 0.1,
            duration: 0.5,
            ease: 'power2.out',
            scrollTrigger: {
              trigger: pricingRef.current,
              start: 'top 75%',
            },
          }
        );
      }

      const benefitCards = benefitsRef.current?.querySelectorAll('.benefit-card');
      if (benefitCards) {
        gsap.fromTo(
          benefitCards,
          { y: 40, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            stagger: 0.1,
            duration: 0.5,
            ease: 'power2.out',
            scrollTrigger: {
              trigger: benefitsRef.current,
              start: 'top 75%',
            },
          }
        );
      }

      gsap.fromTo(
        requirementsRef.current,
        { y: 30, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.6,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: requirementsRef.current,
            start: 'top 80%',
          },
        }
      );

    });

    return () => ctx.revert();
  }, []);

  return (
    <main className="bg-bg-light">
      <SEO
        title="Software para Agencias de Envíos y Transitarias a Cuba | Gratis 6 Meses | Mi Envío Cuba"
        description="Plataforma de gestión para agencias de envíos a Cuba y sus transitarias. Panel web, app para tus clientes y app de almacén y reparto que funciona sin señal. Gratis tus primeros 6 meses; después $600 al año. Solicita una demo gratuita."
        path="/plataforma"
        keywords="software para agencias de envíos a Cuba, plataforma courier Cuba, app para agencias de paquetes Cuba, sistema para agencia de paquetería a Cuba, digitalizar agencia envíos Cuba, dashboard gestión courier, software para transitarias, consolidación de carga a Cuba, manifiestos de contenedor software, rastreo de paquetes para agencias"
      />
      {/* Header */}
      <div ref={headerRef} className="pt-36 sm:pt-44 pb-16 px-4 sm:px-6 lg:px-8 xl:px-12">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-semibold tracking-[-0.03em] text-text-primary mb-6">
            Digitaliza tu agencia de envíos a Cuba.
          </h1>
          <p className="text-xl sm:text-2xl text-text-secondary font-medium leading-snug mb-10">
            MEC es la plataforma de software que las agencias de envíos a Cuba — y las
            transitarias que consolidan su carga — usan para dejar de gestionar todo por
            WhatsApp y operar como una empresa real. Panel web, apps y sistema completo,
            conectados en tiempo real.
          </p>
          <Button
            className="rounded-full bg-brand text-white px-7 h-12 text-[17px] hover:bg-brand-deep transition-colors group"
            onClick={() => document.getElementById('solicitar')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
          >
            Solicitar demo gratuita
            <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
          </Button>
        </div>
      </div>

      {/* Dashboard Features */}
      <div ref={dashboardRef} className="pb-16 px-4 sm:px-6 lg:px-8 xl:px-12">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-[-0.03em] text-text-primary mb-4">
              Mi Dashboard
            </h2>
            <p className="text-lg text-text-secondary max-w-2xl mx-auto">
              El panel web de tu agencia: el centro operativo para el control total
              desde un solo lugar.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {dashboardFeatures.map((feature, index) => (
              <div
                key={index}
                className="feature-card bg-white rounded-[24px] shadow-card p-6"
              >
                <div className="w-12 h-12 rounded-xl bg-brand/10 flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-brand" />
                </div>
                <h3 className="font-heading text-lg font-semibold text-text-primary mb-2">
                  {feature.title}
                </h3>
                <p className="text-text-secondary text-sm">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Admin App Features */}
      <div ref={adminAppRef} className="pb-16 px-4 sm:px-6 lg:px-8 xl:px-12">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-[-0.03em] text-text-primary mb-4">
              Mi Admin
            </h2>
            <p className="text-lg text-text-secondary max-w-2xl mx-auto">
              La app de almacén y reparto: escaneo, entregas y prueba verificada.
              Usada por personal de almacén, repartidores y administradores.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {adminAppFeatures.map((feature, index) => (
              <div
                key={index}
                className="admin-card bg-white rounded-[24px] shadow-card p-6"
              >
                <div className="w-11 h-11 rounded-xl bg-brand/10 flex items-center justify-center mb-4">
                  <feature.icon className="w-5 h-5 text-brand" />
                </div>
                <h3 className="font-heading text-lg font-semibold text-text-primary mb-2">
                  {feature.title}
                </h3>
                <p className="text-text-secondary text-sm">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Transitarias — freight consolidators (Mi Cargo, en camino) */}
      <div id="transitarias" ref={transitariasRef} className="pb-16 px-4 sm:px-6 lg:px-8 xl:px-12 scroll-mt-24">
        <div className="max-w-5xl mx-auto">
          <div className="bg-bg-dark rounded-[32px] p-8 sm:p-10 lg:p-12 text-text-light">
            <div className="grid lg:grid-cols-[1.1fr_1fr] gap-8 lg:gap-12 items-start">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center">
                    <Container className="w-6 h-6 text-brand" />
                  </div>
                  <span className="inline-flex items-center rounded-full bg-white/10 text-text-light text-xs font-semibold px-3 py-1">
                    Mi Cargo · En camino
                  </span>
                </div>
                <h2 className="font-heading text-2xl sm:text-3xl font-semibold mb-4">
                  Para transitarias: de cien agencias a un solo manifiesto
                </h2>
                <p className="text-text-muted leading-relaxed mb-6">
                  Mi Cargo recibe los paquetes de todas las agencias que te envían carga,
                  los consolida por destino y arma el manifiesto del contenedor — con
                  escaneo masivo en almacén y trazabilidad en cada traspaso: recepción,
                  despacho, llegada y entrega. El escaneo masivo ya vive hoy en Mi Admin.
                </p>
                <p className="text-sm text-text-muted mb-6">
                  Las transitarias entran con la <span className="text-text-light font-medium">Suite Empresarial</span>:
                  configuración a la medida de tu almacén y condiciones por volumen.
                </p>
                <Button
                  className="rounded-full bg-brand text-white px-6 h-11 hover:bg-brand-deep transition-colors"
                  onClick={() => {
                    setForm(prev => ({ ...prev, plan: 'Transitaria (Suite Empresarial)' }));
                    document.getElementById('solicitar')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                >
                  Hablar con MEC
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                {transitariaFeatures.map((feature, index) => (
                  <div
                    key={index}
                    className="transitaria-card bg-white/5 border border-white/10 rounded-[20px] p-5"
                  >
                    <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center mb-3">
                      <feature.icon className="w-5 h-5 text-brand" />
                    </div>
                    <h3 className="font-heading text-base font-semibold text-text-light mb-1">
                      {feature.title}
                    </h3>
                    <p className="text-sm text-text-muted leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Ecosystem */}
      <div ref={ecosystemRef} className="pb-16 px-4 sm:px-6 lg:px-8 xl:px-12">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-[-0.03em] text-text-primary mb-4">
              El ecosistema MEC.
            </h2>
            <p className="text-lg text-text-secondary max-w-2xl mx-auto">
              Un sistema integrado diseñado para cubrir todas las necesidades:
              agencias, transitarias, repartidores, comercios y clientes.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
            {ecosystemPlatforms.map((platform, index) => (
              <Link
                key={index}
                to={platform.to}
                className="ecosystem-card bg-white rounded-[24px] shadow-card p-6 text-center block hover:bg-bg-light transition-colors"
              >
                <img
                  src={platform.logo}
                  alt={platform.alt}
                  width="48"
                  height="48"
                  loading="lazy"
                  className="w-12 h-12 rounded-xl shadow-sm mx-auto mb-4"
                />
                <h3 className="font-heading text-lg font-semibold text-text-primary mb-2">
                  {platform.title}
                </h3>
                <p className="text-text-secondary text-sm">
                  {platform.description}
                </p>
                <span className="mt-3 inline-block text-sm font-medium text-brand">Más información ›</span>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Pricing */}
      <div id="planes" ref={pricingRef} className="pb-16 px-4 sm:px-6 lg:px-8 xl:px-12 scroll-mt-24">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-[-0.03em] text-text-primary mb-4">
              Precio
            </h2>
            <p className="text-lg text-text-secondary max-w-2xl mx-auto">
              Gratis tus primeros 6 meses. Completo. Todas las funciones, todas las apps,
              todas tus sucursales — sin tarjeta de crédito para empezar.
            </p>
          </div>
          <div className="max-w-xl mx-auto">
            {pricingPlans.map((plan, index) => (
              <div
                key={index}
                className={`pricing-card bg-white rounded-[28px] shadow-card overflow-hidden ${plan.featured ? 'ring-2 ring-brand' : ''}`}
              >
                {/* Header */}
                <div className={`${plan.color} p-6 text-white`}>
                  <div className="flex items-center gap-3 mb-2">
                    <plan.icon className="w-6 h-6" />
                    <span className="text-xs font-semibold tracking-wider opacity-90">
                      {plan.subtitle}
                    </span>
                  </div>
                  <h3 className="font-heading text-xl font-bold mb-1">
                    {plan.name}
                  </h3>
                  <div className="flex items-baseline gap-1">
                    <span className="font-heading text-4xl font-bold">
                      {plan.price}
                    </span>
                    <span className="opacity-80">{plan.period}</span>
                  </div>
                </div>

                {/* Features */}
                <div className="p-6">
                  <div className="space-y-4">
                    {plan.features.map((featureGroup, fgIndex) => (
                      <div key={fgIndex}>
                        <h4 className="font-heading text-sm font-semibold text-text-primary mb-2">
                          {featureGroup.title}
                        </h4>
                        {featureGroup.items.length > 0 && (
                          <ul className="space-y-1.5">
                            {featureGroup.items.map((item, itemIndex) => (
                              <li
                                key={itemIndex}
                                className="flex items-start gap-2 text-sm text-text-secondary"
                              >
                                <CheckCircle className="w-4 h-4 text-brand flex-shrink-0 mt-0.5" />
                                {item}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Ideal For */}
                  <div className="mt-6 pt-4 border-t border-gray-100">
                    <p className="text-xs text-text-muted uppercase tracking-wider mb-1">
                      Perfil ideal
                    </p>
                    <p className="text-sm text-text-secondary">
                      {plan.ideal}
                    </p>
                  </div>

                  {/* CTA */}
                  <Button
                    className={`w-full rounded-full mt-6 ${
                      plan.featured
                        ? 'bg-brand text-white hover:opacity-90'
                        : 'bg-gray-100 text-text-primary hover:bg-gray-200'
                    } transition-colors`}
                    onClick={() => {
                      setForm(prev => ({ ...prev, plan: 'Plan Estándar (gratis los primeros 6 meses)' }));
                      document.getElementById('solicitar')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }}
                  >
                    Empieza gratis
                  </Button>
                </div>
              </div>
            ))}

            {/* Suite Empresarial — custom tier, priced on request */}
            <div className="pricing-card bg-white rounded-[28px] shadow-card p-6 mt-6 flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-brand/10 flex items-center justify-center flex-shrink-0">
                <Building2 className="w-6 h-6 text-brand" />
              </div>
              <div className="flex-1">
                <h3 className="font-heading text-lg font-semibold text-text-primary mb-1">
                  Suite Empresarial
                </h3>
                <p className="text-sm text-text-secondary">
                  Para operaciones grandes con necesidades a la medida: configuración
                  personalizada, acompañamiento dedicado y condiciones por volumen.
                </p>
              </div>
              <Button
                className="rounded-full bg-gray-100 text-text-primary hover:bg-gray-200 transition-colors flex-shrink-0"
                onClick={() => {
                  setForm(prev => ({ ...prev, plan: 'Suite Empresarial (a consultar)' }));
                  document.getElementById('solicitar')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
              >
                A consultar
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Benefits */}
      <div ref={benefitsRef} className="pb-16 px-4 sm:px-6 lg:px-8 xl:px-12">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-[-0.03em] text-text-primary text-center mb-10">
            Beneficios de ser agencia
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="benefit-card bg-white rounded-[24px] shadow-card p-6 text-center"
              >
                <div className="w-12 h-12 rounded-xl bg-brand/10 flex items-center justify-center mx-auto mb-4">
                  <benefit.icon className="w-6 h-6 text-brand" />
                </div>
                <h3 className="font-heading text-lg font-semibold text-text-primary mb-2">
                  {benefit.title}
                </h3>
                <p className="text-text-secondary text-sm">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Requirements */}
      <div ref={requirementsRef} className="pb-16 px-4 sm:px-6 lg:px-8 xl:px-12">
        <div className="max-w-2xl mx-auto bg-white rounded-[28px] shadow-card p-8">
          <h2 className="font-heading text-xl font-semibold text-text-primary text-center mb-6">
            Requisitos para unirte
          </h2>
          <ul className="space-y-3">
            {requirements.map((req, index) => (
              <li
                key={index}
                className="flex items-center gap-3 text-text-secondary"
              >
                <CheckCircle className="w-5 h-5 text-brand flex-shrink-0" />
                {req}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Agency Application Form */}
      <div id="solicitar" className="pb-16 px-4 sm:px-6 lg:px-8 xl:px-12">
        <div className="max-w-2xl mx-auto bg-white rounded-[28px] shadow-card p-8">
          <h2 className="font-heading text-2xl font-semibold text-text-primary text-center mb-2">
            Solicita información
          </h2>
          <p className="text-text-secondary text-center mb-8 text-sm">
            Cuéntanos sobre tu agencia y te contactamos para comenzar.
          </p>

          {status === 'success' ? (
            <div className="text-center py-8">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
              <h3 className="font-heading text-lg font-semibold text-text-primary mb-2">¡Solicitud enviada!</h3>
              <p className="text-text-secondary text-sm">Nos pondremos en contacto contigo pronto.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1">Nombre completo *</label>
                  <input name="nombre" required value={form.nombre} onChange={handleChange}
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand"
                    placeholder="Tu nombre" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1">Nombre de la agencia *</label>
                  <input name="empresa" required value={form.empresa} onChange={handleChange}
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand"
                    placeholder="Tu agencia" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1">Correo electrónico *</label>
                  <input name="email" type="email" required value={form.email} onChange={handleChange}
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand"
                    placeholder="tu@email.com" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1">Teléfono</label>
                  <input name="telefono" type="tel" value={form.telefono} onChange={handleChange}
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand"
                    placeholder="+1 (555) 000-0000" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1">Ciudad</label>
                  <input name="ciudad" value={form.ciudad} onChange={handleChange}
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand"
                    placeholder="Miami, FL" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1">Plan de interés</label>
                  <select name="plan" value={form.plan} onChange={handleChange}
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand bg-white">
                    <option value="">Seleccionar...</option>
                    <option value="Plan Estándar (gratis los primeros 6 meses)">Plan Estándar – gratis los primeros 6 meses</option>
                    <option value="Transitaria (Suite Empresarial)">Transitaria – consolidación y manifiestos</option>
                    <option value="Suite Empresarial (a consultar)">Suite Empresarial – a consultar</option>
                    <option value="Tengo preguntas antes de empezar">Tengo preguntas antes de empezar</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">Mensaje (opcional)</label>
                <textarea name="mensaje" value={form.mensaje} onChange={handleChange} rows={3}
                  className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand resize-none"
                  placeholder="Cuéntanos sobre tu operación actual..." />
              </div>
              {status === 'error' && (
                <p className="text-red-500 text-sm text-center">Hubo un error. Por favor intenta de nuevo.</p>
              )}
              <Button type="submit" disabled={status === 'loading'}
                className="w-full rounded-full bg-brand text-white hover:opacity-90 transition-opacity">
                {status === 'loading' ? 'Enviando...' : 'Enviar solicitud'}
              </Button>
            </form>
          )}
        </div>
      </div>

      <Footer />
    </main>
  );
}
